import { getStructuresByIds } from "@/firebase/firestore";
import { doc, getDoc } from "firebase/firestore";
import db from "@/firebase/firestore";
import { isSuggestionsV2Enabled, isSuggestionsV2PreferServerMerged } from "@/lib/env";
import { fetchSuggestionsV2MergedFromService, isServerMergedV2Response } from "@/lib/suggestionsV2Client";
import { getSuggestionStatusesForStructure, mergeSuggestionsWithStatuses } from "@/lib/suggestions/SuggestionsRepository";

/**
 * Suggestions V2 Module
 * 
 * Handles fetching and normalizing suggestions data from Firestore sources:
 * 
 * 1. Primary (V2 enabled): Firestore `suggestions` collection document
 * 2. Fallback: Structure document from Firestore (legacy suggestions field)
 * 
 * NOTE: Default behavior uses Firestore directly. When
 * VITE_SUGGESTIONS_V2_PREFER_SERVER_MERGED=1, it will first attempt a
 * backoffice server-merged endpoint and fall back to Firestore.
 * 
 * Normalization:
 * - Handles both V2 format (categories with items) and legacy format (interventi strings)
 * - Generates deterministic IDs using hashIdFromText when missing
 * - Defaults status to "todo" when not specified
 * - Does NOT mutate original data or write to Firestore (read-only for UI)
 */

export type SuggestionStatus = "todo" | "in_progress" | "verifica_in_corso" | "done" | "rejected";

export type SuggestionItemV2 = {
  id: string;
  text: string;
  status: SuggestionStatus;
};

export type SuggestionCategoryV2 = {
  category: string;
  items: SuggestionItemV2[];
};

export type SuggestionsV2 = {
  structureId: string;
  categories: SuggestionCategoryV2[];
  updatedAt?: string | Date | null;
  source: "collection" | "legacy_structure" | "none";
};

export type SuggestionsV2Summary = {
  structureId: string;
  total: number;
  completionRate: number;
  byStatus: {
    todo: number;
    in_progress: number;
    verifica_in_corso: number;
    done: number;
    rejected: number;
  };
  byCategory: Array<{
    category: string;
    total: number;
    done: number;
    todo: number;
    in_progress: number;
    verifica_in_corso: number;
    rejected: number;
  }>;
  updatedAt?: string | Date | null;
  source: "collection" | "legacy_structure" | "none";
};

const DEFAULT_CATEGORY = "general";
const DEFAULT_STATUS: SuggestionStatus = "todo";

const getSuggestionsMode = (): "legacy" | "v2" => (isSuggestionsV2Enabled() ? "v2" : "legacy");

const shouldPreferServerMerged = (): boolean => isSuggestionsV2PreferServerMerged();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isExpectedFirestoreReadError = (error: unknown): boolean => {
  const code = isRecord(error) ? String(error.code ?? error.name ?? '').toLowerCase() : '';
  const message = isRecord(error) ? String(error.message ?? '').toLowerCase() : String(error ?? '').toLowerCase();

  return (
    code === 'permission-denied' ||
    code === 'unauthenticated' ||
    message.includes('permission-denied') ||
    message.includes('missing or insufficient permissions') ||
    message.includes('unauthenticated')
  );
};

const normalizeUpdatedAt = (value: unknown): string | Date | null | undefined => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return value.trim() ? value : null;
  if (typeof value === "number") return new Date(value);
  if (isRecord(value) && typeof value.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  return null;
};

const extractUpdatedAtFromMeta = (meta: unknown): string | Date | null | undefined => {
  if (!isRecord(meta)) return null;
  return normalizeUpdatedAt(meta.updatedAt ?? meta.updated_at ?? meta.updated);
};

export const hashIdFromText = (text: string): string => {
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash) ^ text.charCodeAt(i);
  }
  return `s-${(hash >>> 0).toString(36)}`;
};

const normalizeStatus = (value: unknown): SuggestionStatus => {
  if (typeof value === "string") {
    const raw = value.trim().toLowerCase();
    if (raw === "todo" || raw === "not_started" || raw === "da realizzare" || raw === "pianificato") {
      return "todo";
    }
    if (raw === "in_progress" || raw === "in progress" || raw === "in corso") {
      return "in_progress";
    }
    if (raw === "done" || raw === "completed" || raw === "completo" || raw === "da verificare") {
      return "done";
    }
    if (raw === "rejected" || raw === "rifiutato" || raw === "scartato") {
      return "rejected";
    }
  }
  return DEFAULT_STATUS;
};

const normalizeItemText = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (isRecord(value)) {
    const text =
      value.text ??
      value.description ??
      value.title ??
      value.name ??
      value.intervento ??
      value.item;
    if (typeof text === "string") return text.trim();
  }
  return "";
};

const normalizeItems = (
  input: unknown,
  fallbackStatus: SuggestionStatus
): SuggestionItemV2[] => {
  if (!Array.isArray(input)) return [];
  const items: SuggestionItemV2[] = [];

  input.forEach((entry) => {
    if (typeof entry === "string") {
      const text = entry.trim();
      if (!text) return;
      items.push({
        id: hashIdFromText(text),
        text,
        status: fallbackStatus,
      });
      return;
    }

    if (!isRecord(entry)) return;
    const text = normalizeItemText(entry);
    if (!text) return;
    const status = normalizeStatus(entry.status ?? fallbackStatus);
    const id = typeof entry.id === "string" && entry.id.trim() ? entry.id.trim() : hashIdFromText(text);
    items.push({ id, text, status });
  });

  return items;
};

const normalizeCategoryEntry = (entry: Record<string, unknown>): SuggestionCategoryV2 | null => {
  const category =
    (typeof entry.category === "string" && entry.category.trim()) ||
    (typeof entry.categoria === "string" && entry.categoria.trim()) ||
    DEFAULT_CATEGORY;
  const fallbackStatus = normalizeStatus(entry.status);
  const itemsRaw =
    entry.items ??
    entry.suggestion_items ??
    entry.suggestions ??
    entry.interventi ??
    [];
  const items = normalizeItems(itemsRaw, fallbackStatus);
  if (!items.length) return null;
  return { category, items };
};

const normalizeLegacySuggestions = (raw: unknown[]): SuggestionCategoryV2[] => {
  const grouped = new Map<string, SuggestionItemV2[]>();

  raw.forEach((entry) => {
    if (!isRecord(entry)) return;

    if (Array.isArray(entry.items)) {
      const categoryEntry = normalizeCategoryEntry(entry);
      if (categoryEntry) {
        const existing = grouped.get(categoryEntry.category) || [];
        grouped.set(categoryEntry.category, existing.concat(categoryEntry.items));
      }
      return;
    }

    const category =
      (typeof entry.category === "string" && entry.category.trim()) ||
      (typeof entry.categoria === "string" && entry.categoria.trim()) ||
      (typeof entry.accessibilityCategory === "string" && entry.accessibilityCategory.trim()) ||
      (typeof entry.area === "string" && entry.area.trim()) ||
      DEFAULT_CATEGORY;

    const fallbackStatus = normalizeStatus(entry.status);

    let items: SuggestionItemV2[] = [];
    if (Array.isArray(entry.interventi)) {
      items = normalizeItems(entry.interventi, fallbackStatus);
    } else {
      const text = normalizeItemText(entry);
      if (text) {
        items = [{
          id: hashIdFromText(text),
          text,
          status: fallbackStatus,
        }];
      }
    }

    if (!items.length) return;
    const existing = grouped.get(category) || [];
    grouped.set(category, existing.concat(items));
  });

  return Array.from(grouped.entries()).map(([category, items]) => ({ category, items }));
};

const extractStructureId = (input: unknown): string => {
  if (!isRecord(input)) return "";
  const raw = input.structureId ?? input.structure_id ?? input.id ?? "";
  return String(raw ?? "").trim();
};

const extractUpdatedAt = (input: unknown): string | Date | null | undefined => {
  if (!isRecord(input)) return null;
  return normalizeUpdatedAt(input.updatedAt ?? input.updated_at ?? input.updated);
};

const extractRawArray = (input: unknown): unknown[] | null => {
  if (Array.isArray(input)) return input;
  if (!isRecord(input)) return null;
  if (Array.isArray(input.categories)) return input.categories;
  if (Array.isArray(input.suggestions)) return input.suggestions;
  if (Array.isArray(input.suggestion_items)) return input.suggestion_items;
  if (Array.isArray(input.items)) return input.items;
  return null;
};

const getStatusCountFromRaw = (input: unknown): number => {
  const raw = extractRawArray(input);
  if (!raw) return 0;

  let count = 0;
  raw.forEach((entry) => {
    if (!isRecord(entry)) return;

    const itemsRaw =
      entry.items ??
      entry.suggestion_items ??
      entry.suggestions ??
      entry.interventi ??
      null;

    if (Array.isArray(itemsRaw)) {
      itemsRaw.forEach((item) => {
        if (isRecord(item) && typeof item.status === "string" && item.status.trim()) {
          count += 1;
        }
      });
      return;
    }

    if (typeof entry.status === "string" && entry.status.trim()) {
      count += 1;
    }
  });

  return count;
};

const countItemsWithStatus = (categories: SuggestionCategoryV2[]): number =>
  categories.reduce((total, category) => {
    const withStatus = category.items.filter((item) => typeof item.status === "string" && item.status.trim()).length;
    return total + withStatus;
  }, 0);

const logSuggestionsSource = (source: string, structureId: string, statusCount: number) => {
  if (!import.meta.env.DEV) return;
  console.info(
    `[SuggestionsV2] source=${source} structureId=${structureId} statusCount=${statusCount}`
  );
};

const isServerMergedResponse = (input: { merged?: boolean; isMerged?: boolean }): boolean =>
  input.merged === true || input.isMerged === true;

export const normalizeSuggestions = (input: unknown): SuggestionsV2 => {
  const structureId = extractStructureId(input);
  const updatedAt = extractUpdatedAt(input);
  const raw = extractRawArray(input);
  let categories: SuggestionCategoryV2[] = [];

  if (raw && raw.length) {
    const looksLikeCategories = raw.some((entry) => isRecord(entry) && ("items" in entry || "category" in entry || "categoria" in entry));
    if (looksLikeCategories) {
      const normalizedCategories: SuggestionCategoryV2[] = [];
      raw.forEach((entry) => {
        if (!isRecord(entry)) return;
        const categoryEntry = normalizeCategoryEntry(entry);
        if (categoryEntry) normalizedCategories.push(categoryEntry);
      });
      categories = normalizedCategories.length ? normalizedCategories : normalizeLegacySuggestions(raw);
    } else {
      categories = normalizeLegacySuggestions(raw);
    }
  }

  return {
    structureId,
    categories,
    updatedAt,
    source: "none",
  };
};

/**
 * Flattened suggestion item with category info
 */
export type FlatSuggestionItem = {
  id: string;
  text: string;
  status: SuggestionStatus;
  category: string;
};

/**
 * UI-friendly result type with both categories and flat items
 * 
 * Used after fetching from /nextapi/structures/{id}/suggestions/status
 * or Firestore fallback sources.
 */
export type NormalizedV2Items = {
  categories: SuggestionCategoryV2[];
  flatItems: FlatSuggestionItem[];
  total: number;
  byStatus: {
    todo: number;
    in_progress: number;
    verifica_in_corso: number;
    done: number;
    rejected: number;
  };
};

/**
 * Normalize suggestions to V2 format and provide flat items array
 * Useful for UI components that need both grouped and flat access
 * 
 * @param input - Can be V2 format (categories with items) or legacy format (interventi strings)
 * @returns Normalized categories and flat items array with statistics
 * 
 * @example
 * // From API response (current endpoint)
 * const response = await fetch('/nextapi/structures/123/suggestions/status');
 * const data = await response.json();
 * const normalized = normalizeToV2Items(data);
 * console.log(normalized.flatItems); // All items with category info
 * console.log(normalized.byStatus.todo); // Count of todo items
 * 
 * @example
 * // From legacy format with interventi
 * const legacyData = {
 *   categories: [
 *     { category: "motoria", interventi: ["Rampa", "Ascensore"] }
 *   ]
 * };
 * const normalized = normalizeToV2Items(legacyData);
 * // Will generate IDs: hashIdFromText("Rampa"), hashIdFromText("Ascensore")
 * // Will default status to "todo"
 */
export const normalizeToV2Items = (input: unknown): NormalizedV2Items => {
  const normalized = normalizeSuggestions(input);
  const flatItems: FlatSuggestionItem[] = [];
  
  normalized.categories.forEach((cat) => {
    cat.items.forEach((item) => {
      flatItems.push({
        id: item.id,
        text: item.text,
        status: item.status,
        category: cat.category,
      });
    });
  });

  const byStatus = {
    todo: flatItems.filter((item) => item.status === "todo").length,
    in_progress: flatItems.filter((item) => item.status === "in_progress").length,
    verifica_in_corso: flatItems.filter((item) => item.status === "verifica_in_corso").length,
    done: flatItems.filter((item) => item.status === "done").length,
    rejected: flatItems.filter((item) => item.status === "rejected").length,
  };

  return {
    categories: normalized.categories,
    flatItems,
    total: flatItems.length,
    byStatus,
  };
};

/**
 * Fetch and normalize suggestions in one step
 * Combines fetchSuggestionsV2 + normalizeToV2Items for convenience
 * 
 * @param structureId - The structure ID to fetch suggestions for
 * @returns Normalized items with flat array and statistics
 * 
 * @example
 * const result = await fetchAndNormalizeV2Items('structure-123');
 * if (result.total > 0) {
 *   console.log('Todo items:', result.flatItems.filter(i => i.status === 'todo'));
 * }
 */
export const fetchAndNormalizeV2Items = async (structureId: string): Promise<NormalizedV2Items> => {
  const suggestions = await fetchSuggestionsV2(structureId);
  return normalizeToV2Items(suggestions);
};

/**
 * Read suggestions directly from Firestore suggestions collection
 * This is the primary source for V2 mode
 */
const readFromSuggestionsCollection = async (
  structureId: string
): Promise<Record<string, unknown> | null> => {
  try {
    const ref = doc(db, "suggestions", structureId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return null;
    }

    const data = snap.data() as Record<string, unknown> | undefined;
    const suggestionsRaw = data?.suggestions;

    if (!Array.isArray(suggestionsRaw) || suggestionsRaw.length === 0) {
      return null;
    }

    return {
      structureId,
      suggestions: suggestionsRaw,
      updatedAt: data?.updatedAt ?? data?.updated_at ?? null,
    };
  } catch (err) {
    if (!isExpectedFirestoreReadError(err)) {
      console.warn(`[suggestionsV2] Error reading suggestions collection for ${structureId}:`, err);
    }
    return null;
  }
};

const readLegacySuggestionsFromFirestore = async (
  structureId: string
): Promise<Record<string, unknown> | null> => {
  try {
    const docs = await getStructuresByIds([structureId]);
    const docData = Array.isArray(docs) ? docs[0] : null;
    if (!docData) return null;
    const legacyPayload =
      docData.suggestions ??
      docData.suggestion_items ??
      docData.items ??
      null;
    if (!legacyPayload) return null;
    return {
      structureId,
      suggestions: legacyPayload,
      updatedAt: docData.updatedAt ?? docData.updated_at ?? null,
    };
  } catch {
    return null;
  }
};

export const fetchSuggestionsV2 = async (structureId: string): Promise<SuggestionsV2> => {
  const normalizedStructureId = String(structureId ?? "").trim();
  if (!normalizedStructureId) {
    return { structureId: "", categories: [], updatedAt: null, source: "none" };
  }

  const mode = getSuggestionsMode();
  if (mode === "legacy") {
    const legacy = await readLegacySuggestionsFromFirestore(normalizedStructureId);
    if (!legacy) {
      logSuggestionsSource("legacy", normalizedStructureId, 0);
      return { structureId: normalizedStructureId, categories: [], updatedAt: null, source: "none" };
    }
    const normalized = normalizeSuggestions(legacy);
    const categories = normalized.categories || [];
    logSuggestionsSource("legacy", normalizedStructureId, countItemsWithStatus(categories));
    return {
      ...normalized,
      structureId: normalizedStructureId,
      categories,
      source: categories.length ? "legacy_structure" : "none",
    };
  }

  if (shouldPreferServerMerged()) {
    const payload = await fetchSuggestionsV2MergedFromService(normalizedStructureId);
    if (payload && isServerMergedV2Response(payload)) {
      const rawArray = extractRawArray(payload);
      const hasStatus = getStatusCountFromRaw(payload) > 0;
      const isMerged = isServerMergedResponse(payload) || hasStatus;

      if (rawArray && rawArray.length > 0 && isMerged) {
        const normalized = normalizeSuggestions(payload);
        const categories = normalized.categories || [];
        logSuggestionsSource("server-merged", normalizedStructureId, countItemsWithStatus(categories));
        return {
          ...normalized,
          structureId: normalizedStructureId,
          categories,
          source: "collection",
        };
      }
    }
  }

  // V2 mode: Read from Firestore suggestions collection
  const fromCollection = await readFromSuggestionsCollection(normalizedStructureId);
  if (fromCollection) {
    // Attempt to fetch and merge suggestion statuses from suggestions_status collection
    let mergedSuggestionsRaw = fromCollection.suggestions;
    try {
      const statusMap = await getSuggestionStatusesForStructure(normalizedStructureId);
      if (statusMap.size > 0) {
        mergedSuggestionsRaw = mergeSuggestionsWithStatuses(
          mergedSuggestionsRaw as unknown[],
          statusMap
        );
      }
    } catch (err) {
      if (!isExpectedFirestoreReadError(err)) {
        console.warn(
          `[suggestionsV2] Failed to fetch/merge suggestion statuses for ${normalizedStructureId}:`,
          err
        );
      }
      // Fallback to base suggestions without merging (safe default)
    }

    const normalized = normalizeSuggestions({
      ...fromCollection,
      suggestions: mergedSuggestionsRaw,
    });
    const categories = normalized.categories || [];
    logSuggestionsSource("client-merged", normalizedStructureId, countItemsWithStatus(categories));
    return {
      ...normalized,
      structureId: normalizedStructureId,
      categories,
      source: "collection",
    };
  }

  // Fallback to legacy structure document
  const legacy = await readLegacySuggestionsFromFirestore(normalizedStructureId);
  if (!legacy) {
    logSuggestionsSource("legacy", normalizedStructureId, 0);
    return { structureId: normalizedStructureId, categories: [], updatedAt: null, source: "none" };
  }

  const normalized = normalizeSuggestions(legacy);
  const categories = normalized.categories || [];
  logSuggestionsSource("legacy", normalizedStructureId, countItemsWithStatus(categories));
  return {
    ...normalized,
    structureId: normalizedStructureId,
    categories,
    source: categories.length ? "legacy_structure" : "none",
  };
};
const normalizeSummaryResponse = (data: unknown): SuggestionsV2Summary | null => {
  if (!isRecord(data)) return null;

  const structureId = String(data.structureId ?? data.structure_id ?? "").trim();
  const total = typeof data.total === "number" ? data.total : (typeof data.totalItems === "number" ? data.totalItems : 0);
  const completionRate = typeof data.completionRate === "number" ? data.completionRate : (typeof data.completion_rate === "number" ? data.completion_rate : 0);

  const byStatus = isRecord(data.byStatus)
    ? {
        todo: typeof data.byStatus.todo === "number" ? data.byStatus.todo : 0,
        in_progress: typeof data.byStatus.in_progress === "number" ? data.byStatus.in_progress : 0,
        verifica_in_corso: typeof data.byStatus.verifica_in_corso === "number" ? data.byStatus.verifica_in_corso : 0,
        done: typeof data.byStatus.done === "number" ? data.byStatus.done : 0,
        rejected: typeof data.byStatus.rejected === "number" ? data.byStatus.rejected : 0,
      }
    : { todo: 0, in_progress: 0, verifica_in_corso: 0, done: 0, rejected: 0 };

  const byCategory = Array.isArray(data.byCategory)
    ? data.byCategory
        .filter((item): item is Record<string, unknown> => isRecord(item))
        .map((item) => ({
          category: String(item.category ?? "general").trim(),
          total: typeof item.total === "number" ? item.total : 0,
          done: typeof item.done === "number" ? item.done : 0,
          todo: typeof item.todo === "number" ? item.todo : 0,
          in_progress: typeof item.in_progress === "number" ? item.in_progress : 0,
          verifica_in_corso: typeof item.verifica_in_corso === "number" ? item.verifica_in_corso : 0,
          rejected: typeof item.rejected === "number" ? item.rejected : 0,
        }))
    : [];

  const updatedAt = normalizeUpdatedAt(data.updatedAt ?? data.updated_at);
  const source = typeof data.source === "string" && (data.source === "collection" || data.source === "legacy_structure") 
    ? data.source 
    : "none";

  return {
    structureId,
    total,
    completionRate,
    byStatus,
    byCategory,
    updatedAt,
    source,
  };
};

export const fetchSuggestionsV2Summary = async (structureId: string): Promise<SuggestionsV2Summary> => {
  const normalizedStructureId = String(structureId ?? "").trim();
  if (!normalizedStructureId) {
    return {
      structureId: "",
      total: 0,
      completionRate: 0,
      byStatus: { todo: 0, in_progress: 0, verifica_in_corso: 0, done: 0, rejected: 0 },
      byCategory: [],
      updatedAt: null,
      source: "none",
    };
  }

  const mode = getSuggestionsMode();
  if (mode === "legacy") {
    // In legacy mode, fetch full data and compute summary
    const fullData = await fetchSuggestionsV2(normalizedStructureId);
    const allItems = fullData.categories.flatMap((cat) => cat.items);
    const total = allItems.length;
    const byStatus = {
      todo: allItems.filter((item) => item.status === "todo").length,
      in_progress: allItems.filter((item) => item.status === "in_progress").length,
      verifica_in_corso: allItems.filter((item) => item.status === "verifica_in_corso").length,
      done: allItems.filter((item) => item.status === "done").length,
      rejected: allItems.filter((item) => item.status === "rejected").length,
    };
    const effectiveTotal = Math.max(0, total - byStatus.rejected);
    const completionRate = effectiveTotal > 0 ? Math.round((byStatus.done / effectiveTotal) * 100) : 0;
    const byCategory = fullData.categories.map((cat) => ({
      category: cat.category,
      total: cat.items.length,
      done: cat.items.filter((item) => item.status === "done").length,
      todo: cat.items.filter((item) => item.status === "todo").length,
      in_progress: cat.items.filter((item) => item.status === "in_progress").length,
      verifica_in_corso: cat.items.filter((item) => item.status === "verifica_in_corso").length,
      rejected: cat.items.filter((item) => item.status === "rejected").length,
    }));

    return {
      structureId: normalizedStructureId,
      total,
      completionRate,
      byStatus,
      byCategory,
      updatedAt: fullData.updatedAt,
      source: fullData.source,
    };
  }

  // V2 mode: Fetch full data and compute summary (no API available)
  const fullData = await fetchSuggestionsV2(normalizedStructureId);
  const allItems = fullData.categories.flatMap((cat) => cat.items);
  const total = allItems.length;
  const byStatus = {
    todo: allItems.filter((item) => item.status === "todo").length,
    in_progress: allItems.filter((item) => item.status === "in_progress").length,
    verifica_in_corso: allItems.filter((item) => item.status === "verifica_in_corso").length,
    done: allItems.filter((item) => item.status === "done").length,
    rejected: allItems.filter((item) => item.status === "rejected").length,
  };
  const effectiveTotal = Math.max(0, total - byStatus.rejected);
  const completionRate = effectiveTotal > 0 ? Math.round((byStatus.done / effectiveTotal) * 100) : 0;
  const byCategory = fullData.categories.map((cat) => ({
    category: cat.category,
    total: cat.items.length,
    done: cat.items.filter((item) => item.status === "done").length,
    todo: cat.items.filter((item) => item.status === "todo").length,
    in_progress: cat.items.filter((item) => item.status === "in_progress").length,
    verifica_in_corso: cat.items.filter((item) => item.status === "verifica_in_corso").length,
    rejected: cat.items.filter((item) => item.status === "rejected").length,
  }));

  return {
    structureId: normalizedStructureId,
    total,
    completionRate,
    byStatus,
    byCategory,
    updatedAt: fullData.updatedAt,
    source: fullData.source,
  };
};